import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlay, FaChevronDown, FaWifi, FaTv, FaCamera, FaBolt, FaPenNib, FaHdd, FaArrowRight } from 'react-icons/fa';
import useScrollAnimation from './useScrollAnimation';

const heroModels = [
  {
    id: 'i18-pro',
    name: 'Peace i18 PRO',
    tag: '5G Flagship',
    tagline: 'Beyond Power. Beyond Limits.',
    videoSrc: '/Photo/video&photo/peace-i17air.mp4',
    posterImg: '/Photo/i17-pro-hero.png',
    specs: [
      { icon: <FaWifi className="minimal-spec-icon" />, label: '5G Ultra-Fast' },
      { icon: <FaTv className="minimal-spec-icon" />, label: '6.61" 120Hz FHD+' },
      { icon: <FaCamera className="minimal-spec-icon" />, label: '20MP AI Powered' },
      { icon: <FaBolt className="minimal-spec-icon" />, label: '5000 mAh Battery' }
    ]
  },
  {
    id: 'nova-17-pro',
    name: 'Peace NOVA 18 PRO',
    tag: '50MP Clarity',
    tagline: 'The Ultimate Smartphone Experience.',
    videoSrc: '/Photo/video&photo/Nova 17 pro.mp4',
    posterImg: '/Photo/nova-17-pro-hero.jpg',
    specs: [
      { icon: <FaCamera className="minimal-spec-icon" />, label: '50MP Master Camera' },
      { icon: <FaTv className="minimal-spec-icon" />, label: '6.56" HD+ Display' },
      { icon: <FaBolt className="minimal-spec-icon" />, label: '5000 mAh Inbuilt' },
      { icon: <FaHdd className="minimal-spec-icon" />, label: '4GB+64GB Storage' }
    ]
  },
  {
    id: 'sc26',
    name: 'Peace SC26',
    tag: 'Built-in Stylus',
    tagline: 'Performance Meets Peace.',
    videoSrc: '/Photo/video&photo/peace-sc26 .mp4',
    posterImg: '/Photo/sc26-hero.jpg',
    specs: [
      { icon: <FaPenNib className="minimal-spec-icon" />, label: 'Built-in Stylus Pen' },
      { icon: <FaTv className="minimal-spec-icon" />, label: '6.56" HD+ Screen' },
      { icon: <FaBolt className="minimal-spec-icon" />, label: '5000 mAh Endurance' },
      { icon: <FaHdd className="minimal-spec-icon" />, label: 'Up to 6GB RAM' }
    ]
  }
];

const HeroSection = () => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.1 });
  const [activeModelIndex, setActiveModelIndex] = useState(1);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const currentModel = heroModels[activeModelIndex];

  return (
    <section 
      ref={sectionRef} 
      className={`hero-apple-section minimal-hero ${isVisible ? 'hero-in-view' : ''}`}
      id="hero"
    >
      {/* Ambient background glow */}
      <div className="hero-ambient-glow" />
      <div className="hero-radial-beam" />

      {/* Prominent High-Contrast 3 Model Switcher Bar */}
      <div className="hero-model-switch-container animate-item" style={{ animationDelay: '0.05s' }}>
        <div className="hero-model-switch-bar prominent-switch-bar">
          {heroModels.map((model, idx) => {
            const isActive = activeModelIndex === idx;
            return (
              <button
                key={model.id}
                type="button"
                className={`hero-model-tab prominent-tab ${isActive ? 'active' : ''}`}
                onClick={() => setActiveModelIndex(idx)}
                aria-pressed={isActive}
              >
                <span className="prominent-tab-name">{model.name}</span>
                <span className="prominent-tab-badge">{model.tag}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Background Media Container */}
      <div className="hero-media-wrapper">
        <video
          key={currentModel.id}
          className="hero-bg-video loaded"
          autoPlay
          loop
          muted
          playsInline
          poster={currentModel.posterImg}
        >
          <source src={currentModel.videoSrc} type="video/mp4" />
          <img 
            src={currentModel.posterImg} 
            alt={currentModel.name} 
            className="hero-fallback-img"
          />
        </video>
        <div className="hero-media-overlay" />
      </div>

      {/* Main Hero Content: Minimalist, Airy & High Contrast */}
      <div className="hero-container minimal-hero-container" key={currentModel.id}>
        
        {/* Core Model Headline */}
        <h1 className="hero-title animate-item" style={{ animationDelay: '0.15s' }}>
          {currentModel.name}
        </h1>

        {/* Centered Device Visual */}
        <div className="hero-device-showcase minimal-device-showcase animate-item mx-auto text-center" style={{ animationDelay: '0.25s' }}>
          <div className="device-frame-glow minimal-frame-glow">
            <img 
              src={currentModel.posterImg} 
              alt={currentModel.name} 
              className="hero-main-device-img mx-auto"
              loading="eager"
            />
          </div>
        </div>

        {/* Short, Punchy Subheadline directly below image */}
        <p className="hero-tagline minimal-hero-tagline animate-item text-center" style={{ animationDelay: '0.35s' }}>
          {currentModel.tagline}
        </p>

        {/* Minimalist Key Specs Strip directly below tagline */}
        <div className="minimal-specs-strip animate-item" style={{ animationDelay: '0.45s' }}>
          {currentModel.specs.map((spec, i) => (
            <div key={i} className="minimal-spec-item">
              {spec.icon}
              <span>{spec.label}</span>
            </div>
          ))}
        </div>

        {/* Exploration Action Buttons */}
        <div className="hero-actions animate-item" style={{ animationDelay: '0.55s' }}>
          <Link to={`/products/${currentModel.id}`} className="apple-btn apple-btn-primary prominent-cta-btn">
            Explore {currentModel.name} <FaArrowRight className="ms-2 btn-icon" />
          </Link>
          <button 
            type="button" 
            onClick={() => setShowVideoModal(true)} 
            className="apple-btn apple-btn-secondary"
            aria-label={`Watch ${currentModel.name} video`}
          >
            <FaPlay className="btn-icon" /> Watch Film
          </button>
        </div>

        {/* Scroll Indicator */}
        <a href="#highlights" className="hero-scroll-indicator" aria-label="Scroll to explore">
          <span className="scroll-text">Explore Specs & Comparison</span>
          <FaChevronDown className="scroll-arrow" />
        </a>
      </div>

      {/* Video Modal Player */}
      {showVideoModal && (
        <div className="apple-modal-backdrop" onClick={() => setShowVideoModal(false)}>
          <div className="apple-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close-btn" 
              onClick={() => setShowVideoModal(false)}
              aria-label="Close modal"
            >
              &times;
            </button>
            <div className="video-responsive-box">
              <video controls autoPlay playsInline className="modal-video-element">
                <source src={currentModel.videoSrc} type="video/mp4" />
                Your browser does not support HTML5 video.
              </video>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
